import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, Package, Home, Download, ExternalLink } from 'lucide-react';
import { CartItem, Order } from '../types';
import axios from 'axios';

interface PurchaseDetails {
  sessionId: string;
  items: CartItem[];
  total: number;
  customerInfo: Order['customerInfo'];
}

interface DownloadFile {
  fileName: string;
  url: string;
}

const ProductDownloads: React.FC<{ sessionId: string; productId: string }> = ({
  sessionId,
  productId,
}) => {
  const [files, setFiles] = useState<DownloadFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDownloads = async () => {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
        const response = await fetch(
          `${API_BASE_URL}/downloads/session/${sessionId}/product/${productId}`
        );
        const data = await response.json();

        if (data.success && data.data.files?.length) {
          setFiles(data.data.files);
        } else {
          setError(data.message || 'No files found');
        }
      } catch {
        setError('Failed to load download links');
      } finally {
        setLoading(false);
      }
    };

    fetchDownloads();
  }, [sessionId, productId]);

  if (loading) {
    return <p className="text-xs text-gray-500">Loading downloads...</p>;
  }

  if (error) {
    return <p className="text-xs text-red-500">{error}</p>;
  }

  return (
    <div className="flex flex-col items-end gap-1.5 w-full">
      {files.map((file) => (
        <a
          key={file.fileName}
          href={file.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md hover:opacity-80 transition-colors w-full justify-center sm:w-auto"
          style={{ backgroundColor: '#39ff14', color: '#000' }}
        >
          <Download className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="truncate">{file.fileName}</span>
          <ExternalLink className="h-3 w-3 flex-shrink-0 opacity-60" />
        </a>
      ))}
      <p className="text-xs text-gray-400 text-right">Tap to open &amp; save — works on mobile</p>
    </div>
  );
};

const OrderConfirmation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [purchase, setPurchase] = useState<PurchaseDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!sessionId) {
        setError('No order reference found. Use the link from your payment confirmation.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
        const response = await axios.get(`${API_BASE_URL}/stripe/session/${sessionId}/purchase`);
        setPurchase(response.data.data);
      } catch (err) {
        setError('Failed to load order details');
        console.error('Error fetching order:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [sessionId]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const items = purchase?.items ?? [];
  const total = purchase?.total ?? 0;

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded w-1/3 mb-6"></div>
          <div className="h-64 bg-gray-300 rounded mb-6"></div>
          <div className="h-32 bg-gray-300 rounded"></div>
        </div>
      </div>
    );
  }

  if (error || items.length === 0 || !sessionId) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-2">Error loading order</div>
          <p className="text-gray-600">{error || 'Order not found'}</p>
          <p className="text-gray-500 text-sm mt-2">
            If you just paid, wait a moment and refresh this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <CheckCircle className="h-16 w-16 mx-auto mb-4" style={{ color: '#39ff14' }} />
        <h1 className="text-3xl font-bold mb-2 tracking-wider" style={{ color: '#39ff14', fontFamily: "'911 Porscha', sans-serif", fontStyle: 'italic' }}>ORDER CONFIRMED!</h1>
        <p className="text-gray-600">
          Thank you for your purchase. Your guitar tabs are ready to download below.
        </p>
        <p className="text-gray-500 text-sm mt-1">Total: {formatPrice(total)}</p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 tracking-wider" style={{ color: '#39ff14', fontFamily: "'911 Porscha', sans-serif", fontStyle: 'italic' }}>ORDER ITEMS</h2>
        <div className="space-y-4">
          {items.map((item) => (
            <div key={item.productId} className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center space-x-4 flex-1">
                <img
                  src={item.product?.image || 'https://via.placeholder.com/64x64?text=No+Image'}
                  alt={item.product?.name || 'Product'}
                  className="w-16 h-16 object-cover rounded-md flex-shrink-0"
                  draggable={false}
                />
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900">
                    {item.product?.name || 'Product'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {item.product?.category || 'Category'}
                  </p>
                  <p className="text-sm font-medium text-gray-900">
                    {formatPrice(item.product?.price || 0)} x {item.quantity}
                  </p>
                </div>
              </div>
              {item.product?.hasDownload && (
                <ProductDownloads sessionId={sessionId} productId={item.productId} />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg p-6 mb-6" style={{ backgroundColor: '#1a1a1a' }}>
        <h2 className="text-lg font-semibold mb-4 tracking-wider" style={{ color: '#39ff14', fontFamily: "'911 Porscha', sans-serif", fontStyle: 'italic' }}>WHAT'S NEXT?</h2>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <Download className="h-5 w-5 flex-shrink-0" style={{ color: '#39ff14' }} />
            <span className="text-sm" style={{ color: '#e0e0e0' }}>Tap each download link above to open your files — on mobile, use Share → Save to Files</span>
          </div>
          <div className="flex items-center space-x-3">
            <Package className="h-5 w-5 flex-shrink-0" style={{ color: '#39ff14' }} />
            <span className="text-sm" style={{ color: '#e0e0e0' }}>Files include Guitar Pro files (.gp) and PDFs for easy viewing</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          onClick={() => { window.location.href = '/'; }}
          className="flex items-center justify-center px-6 py-3 rounded-md hover:opacity-80 transition-colors font-bold"
          style={{ backgroundColor: '#39ff14', color: '#000' }}
        >
          <Home className="h-4 w-4 mr-2" />
          CONTINUE SHOPPING
        </button>
      </div>
    </div>
  );
};

export default OrderConfirmation;
